import moment from 'moment';
import { v1 as uuidv1 } from 'uuid';
import { getGeolocationSources } from './geolocation';
import { CONFIDENCE, REASON } from './usbSerialNumber';

export const CONNECTED = 'CONNECTED';
export const DISCONNECTED = 'DISCONNECTED';
export const CODE_BUILD = 'CODE_BUILD';

// The selected port travels through the app as its system name only.
export const findAgentPortByName = (ports, name) => (ports || []).find((port) => port && port.Name === name) || null;

/**
 * Identity of a port for as long as it stays listed. The name is unique within the
 * agent that reported it and is never changed once assigned, which is what the
 * diff needs: the serial number of a board is read after its port has already been
 * published, so a key that included it would change value for the same physical
 * board. The diff would then report one board leaving and another arriving, and
 * the claimedBoards entry under the old key would be left behind, where no later
 * disconnection can ever clear it.
 */
const portKey = (port) => port.Name || '';

/**
 * The web agents keep a single port list and mutate it in place with push/splice,
 * so the array stored in redux is always the same instance and the previous props
 * already reflect the change. A snapshot of what was reported is the only baseline
 * that survives that mutation.
 */
export const snapshotAgentPorts = (ports) => (ports || []).filter(Boolean).map((port) => ({
  Name: port.Name,
  SerialNumber: port.SerialNumber,
  VendorID: port.VendorID,
  ProductID: port.ProductID,
  // Read over WebUSB, and kept apart from SerialNumber, which is the one the
  // desktop agent reports. Either fills serial_string, that one first.
  UsbSerialNumber: port.UsbSerialNumber,
  SerialConfidence: port.SerialConfidence,
  SerialMatch: port.SerialMatch,
  // Why the read came back without one, which the event carries even though the
  // serial itself is null. See serialFields.
  SerialReason: port.SerialReason,
  // Read off the board itself over the Educabot protocol: the production number
  // in its EEPROM, and the hardware serial its firmware derives from the chip.
  // These are what identity means from now on; the two above are what a board
  // whose transport cannot speak the protocol still has.
  SerialString: port.SerialString,
  SerialSource: port.SerialSource,
  HardwareSerial: port.HardwareSerial,
  // See pendingIdentity.
  IdentityPending: port.IdentityPending,
}));

/**
 * A board whose identity is still being worked out: the firmware is being
 * installed, its EEPROM is being read, or a number is being asked for. It is
 * listed and usable -- it shows in the selector and code can be sent to it --
 * but it is held back from the connection diff, so no CONNECTED goes out for it
 * yet.
 *
 * The reason to wait is that the serial is what usage gets grouped by. Reporting
 * the board the moment it is listed and correcting it afterwards was the old
 * shape, and it needs the correction to reach the same event; a board reported
 * once, already identified, needs nothing.
 *
 * Nobody is trusted to always answer: whoever sets this has to clear it, and the
 * ones that hold it also clear it on a deadline, so a board is never lost to a
 * step that never finished. See BoardIdentity.
 */
const pendingIdentity = (port) => !!(port && port.IdentityPending);

const settled = (ports) => (ports || []).filter((port) => !pendingIdentity(port));

const portsByKey = (ports) => {
  const map = new Map();
  (ports || []).forEach((port) => {
    if (port) map.set(portKey(port), port);
  });
  return map;
};

// Returns the ports that appeared and the ones that went away between two lists.
export const diffAgentPorts = (oldPorts, newPorts) => {
  const previous = portsByKey(oldPorts);
  const current = portsByKey(newPorts);
  const connected = [];
  const disconnected = [];

  current.forEach((port, key) => {
    if (!previous.has(key)) connected.push(port);
  });
  previous.forEach((port, key) => {
    if (!current.has(key)) disconnected.push(port);
  });

  return { connected, disconnected };
};

/**
 * Ports that were already listed and whose serial number turned up since the last
 * look. The connection diff cannot see these, by design: it keys ports by name so
 * that a serial arriving late does not read as one board leaving and another
 * arriving. This is the other half of that decision, the one that lets the late
 * read still be reported.
 *
 * One path produces these: a board whose identity took longer than the deadline
 * that holds its CONNECTED back, so the event went out with an empty serial and
 * the number turned up afterwards. Every other board is reported once, with its
 * identity already in hand.
 */
const identityOf = (port) => (port ? (port.SerialString || port.UsbSerialNumber) : null);

export const diffResolvedSerials = (oldPorts, newPorts) => {
  const previous = portsByKey(oldPorts);
  const resolved = [];
  portsByKey(newPorts).forEach((port, key) => {
    const before = previous.get(key);
    // A board still holding its CONNECTED back has no event to complete yet.
    if (pendingIdentity(port) || pendingIdentity(before)) return;
    if (before && !identityOf(before) && identityOf(port)) resolved.push(port);
  });
  return resolved;
};


/**
 * Where the app is being used from: the full URL, so the event carries both the
 * deployment (robots.educabot.com, robots.createc.bue.edu.ar, a local build) and
 * the screen it came from.
 *
 * The Electron build loads the page from file:// and its href is the install path
 * on disk — no deployment in it, and it carries the system user's name — so there
 * the themed domain stands in for the origin, followed by the route. That build
 * routes with HashRouter (see app/index.jsx), so its route lives in the hash.
 */
const pageUrl = () => {
  if (typeof window === 'undefined' || !window.location) return process.env.THEME_DOMAIN || '';
  if (!window.location.host) return `${process.env.THEME_DOMAIN || ''}${window.location.hash || ''}`;
  return window.location.href;
};

// The parts are independent: the position travels even for an anonymous user, and
// the user data travels even when the position could not be resolved.
const buildContext = (userData, geolocation, extra) => ({
  ...(userData ? {
    userId: userData.userId,
    name: userData.name,
    lastName: userData.lastName,
    email: userData.email,
  } : {}),
  url: pageUrl(),
  geolocation: geolocation || [],
  ...extra,
});

/**
 * The serial number of the board and where it came from. Two transports can supply
 * one: the desktop agent lists it alongside every port it reports, and WebUSB is
 * read in the browser. The desktop agent's wins.
 *
 * serial_string holds the serial itself, and null when there is none. It used to
 * carry a descriptor built out of the port name and the usb ids for boards without
 * one, which was the same string for every board of a model in every school, so
 * the backend was grouping different boards under one key. A null says plainly
 * that the board is unknown.
 *
 * serial_reason says why there is none, and the values live in REASON. Unlike the
 * match it is worth carrying, because a null serial_string has causes that call
 * for different answers and none of them can be told apart afterwards: 'absent' is
 * a board that will never have one, 'unavailable' is a read that did not happen
 * and may still, and 'masked' is a browser that answered with a serial it made up
 * - the reading that would otherwise be counted as a board without one, and the
 * only one that is fixed by the user rather than by us. It is 'none' whenever
 * there is a serial, so the field is always there to group by.
 *
 * How the port and the device were tied together is deliberately left out. It is
 * recorded on the port as SerialMatch and the values live in MATCH, so it can be
 * added here later, but a field nobody acts on is a field every reader of the data
 * has to work out the meaning of first.
 */
const serialFields = (port) => {
  /**
   * The production number read off the board's own EEPROM wins over everything
   * else. It is the one Educabot assigns and the one the manufacturing tool
   * burns in, so it is the same number the board is known by outside this app;
   * the others identify a chip or a usb descriptor, which is only ever a stand-in
   * for it.
   *
   * Where it came from travels as serial_source, and its values live in SOURCE:
   * 'eeprom' for one that was already there, 'assigned' for the session that put
   * it there, and 'webusb' for the few boards delivered with their number in the
   * ch340 and nothing in the EEPROM.
   */
  if (port && port.SerialString) {
    return {
      serial_string: port.SerialString,
      serial_confidence: CONFIDENCE.HIGH,
      serial_source: port.SerialSource || 'eeprom',
      serial_reason: REASON.NONE,
      hardware_serial: port.HardwareSerial || null,
    };
  }
  if (port && port.SerialNumber) {
    return {
      serial_string: port.SerialNumber,
      serial_confidence: CONFIDENCE.HIGH,
      serial_source: 'desktop_agent',
      serial_reason: REASON.NONE,
      hardware_serial: port.HardwareSerial || null,
    };
  }
  if (port && port.UsbSerialNumber) {
    return {
      serial_string: port.UsbSerialNumber,
      serial_confidence: port.SerialConfidence || CONFIDENCE.HIGH,
      serial_source: 'webusb',
      serial_reason: REASON.NONE,
      hardware_serial: (port && port.HardwareSerial) || null,
    };
  }
  return {
    serial_string: null,
    serial_confidence: CONFIDENCE.NONE,
    serial_source: null,
    serial_reason: (port && port.SerialReason) || REASON.UNAVAILABLE,
    /**
     * Carried even when there is no production number, and on purpose: a board
     * with the firmware always has one of these, so it is what tells a board
     * that could not be numbered from one that was never reached at all.
     */
    hardware_serial: (port && port.HardwareSerial) || null,
  };
};

/**
 * `contextExtra` goes inside context, alongside the user and the position;
 * `fields` sits at the top level of the event, next to the serial and the action.
 */
const buildEvent = (port, action, userData, geolocation, timestamp, contextExtra, fields) => ({
  id: uuidv1(),
  timestamp,
  ...serialFields(port),
  ...fields,
  action,
  context: buildContext(userData, geolocation, contextExtra),
});

/**
 * Boards already reported as connected, shared by every tab inside the app. Two
 * tabs whose agents can see the same board both notice the change, and both would
 * report it: the first one to claim a board reports it, the rest stay quiet.
 *
 * The value is `true` while the board is being claimed and becomes the id and the
 * timestamp of the CONNECTED event once that event exists, so a serial number
 * turning up later can be sent as that same event rather than as a new one. One
 * map and not two: the claim and the event it stands for are put up and taken down
 * together, and keeping them apart would only be two things to keep in step. It
 * has to be shared rather than held per panel, because the panel that notices the
 * serial need not be the one that reported the board.
 *
 * A module-level object, so this only covers the tabs of one page. Two browser
 * windows on the same origin each keep their own, and both report the same board.
 * Fixing that needs something that crosses windows, such as a BroadcastChannel;
 * it matters more now that the serial number is what usage gets grouped by.
 */
const claimedBoards = {};

/**
 * Keeps only the ports whose change has not been reported yet, and records the
 * claim. It runs before the position is resolved, so two tabs reaching this in the
 * same tick cannot both claim the same board.
 */
const claimPorts = (ports, action) => ports.filter((port) => {
  const key = portKey(port);
  if (action === CONNECTED) {
    if (claimedBoards[key]) return false;
    claimedBoards[key] = true;
    return true;
  }
  if (!claimedBoards[key]) return false;
  delete claimedBoards[key];
  return true;
});

/**
 * Builds one event per board that was plugged or unplugged, enriched with the
 * position reported by the browser (geo).
 * Resolves to an empty array when nothing changed or when another tab already
 * reported the change.
 */
export const buildBoardConnectionEvents = (oldPorts, newPorts, userData) => {
  /**
   * Boards whose identity is still being worked out are left out of both sides
   * of the diff, so one of them neither arrives nor leaves: it simply is not
   * there yet as far as reporting goes. Once its identity settles it turns up as
   * an arrival, and its CONNECTED goes out already carrying the number.
   */
  const { connected, disconnected } = diffAgentPorts(settled(oldPorts), settled(newPorts));
  const plugged = claimPorts(connected, CONNECTED);
  const unplugged = claimPorts(disconnected, DISCONNECTED);
  if (!plugged.length && !unplugged.length) return Promise.resolve([]);

  // Taken before resolving the position, which may take a few seconds.
  const timestamp = moment().format('YYYY-MM-DDTHH:mm:ssZ');

  return getGeolocationSources().then((geolocation) => ([
    ...plugged.map((port) => {
      const event = buildEvent(port, CONNECTED, userData, geolocation, timestamp);
      // Held onto so a serial number read after this point can be sent as this
      // same event. The claim is already in place; this fills in what it stands
      // for.
      claimedBoards[portKey(port)] = { id: event.id, timestamp };
      return event;
    }),
    ...unplugged.map((port) => buildEvent(port, DISCONNECTED, userData, geolocation, timestamp)),
  ]));
};

/**
 * Completes the connection events of boards whose serial number turned up after
 * they had already been reported, which is the board that was plugged in before
 * the page opened: it gets listed, and its connection reported, before the flow
 * that reads the serial has been walked through.
 *
 * These are not events of their own. Each one is the CONNECTED of that board
 * again, carrying the id and the timestamp it went out with, and the serial filled
 * in. The backend has to take them as an update of that event and not as a second
 * connection, the id being what says which one; in exchange there is a single row
 * per connection and nothing to join.
 *
 * Nothing is sent when that event is out of reach, which is the case when another
 * browser window reported the board or the page has reloaded since. There is no id
 * to correct, and a new one would read as the board having connected twice; the
 * serial travels with the next event of that board instead.
 *
 * Resolves to an empty array when no listed board gained a serial, which is the
 * usual case.
 */
export const buildSerialCompletionEvents = (oldPorts, newPorts, userData) => {
  const claimOf = (port) => {
    const claim = claimedBoards[portKey(port)];
    return (claim && typeof claim === 'object') ? claim : null;
  };
  const resolved = diffResolvedSerials(oldPorts, newPorts).filter(claimOf);
  if (!resolved.length) return Promise.resolve([]);

  return getGeolocationSources().then((geolocation) => (
    resolved.map((port) => {
      const claim = claimOf(port);
      return buildEvent(port, CONNECTED, userData, geolocation, claim.timestamp, undefined, {
        id: claim.id,
      });
    })
  ));
};

/**
 * Builds the event reported when code is uploaded to a board. Same payload as a
 * connection event, with the uploaded code added to the context.
 */
export const buildCodeBuildEvent = (port, code, userData) => {
  // Taken before resolving the position, which may take a few seconds.
  const timestamp = moment().format('YYYY-MM-DDTHH:mm:ssZ');

  return getGeolocationSources().then((geolocation) => buildEvent(port, CODE_BUILD, userData, geolocation, timestamp, { code }));
};
